import type { DecisionEngineJourneyDto, PdlAffordabilityJourneyDto } from '../api/admin';
import {
  buildPdlAdjudicatorTraceRows,
  getPdlTraceLabel,
  groupPdlAdjudicatorTraceRows,
  type PdlAdjudicatorTraceRow,
} from './pdlAdjudicatorCopy';

export type DecisionPanelKvRow = {
  label: string;
  value?: string | number | null;
  emphasize?: boolean;
  always?: boolean;
};

export function formatMoneyFromCents(cents?: number | null): string | null {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return null;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function formatRatioPercent(value?: number | null): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const pct = value <= 1 ? value * 100 : value;
  return `${pct.toFixed(1).replace(/\.0$/, '')}%`;
}

function formatYesNo(value?: boolean | null): string | null {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return null;
}

export function buildPdlAffordabilityRows(
  pdl?: PdlAffordabilityJourneyDto | null,
  decisionEngine?: DecisionEngineJourneyDto | null,
): DecisionPanelKvRow[] {
  const monthlyIncome =
    pdl?.monthlyIncome ?? decisionEngine?.monthlyIncome ?? null;

  return [
    {
      label: 'Monthly income (bank)',
      value:
        typeof monthlyIncome === 'number'
          ? new Intl.NumberFormat('en-CA', {
              style: 'currency',
              currency: 'CAD',
              maximumFractionDigits: 0,
            }).format(monthlyIncome)
          : null,
      emphasize: true,
      always: true,
    },
    {
      label: 'Existing monthly debt (ELOD)',
      value: formatMoneyFromCents(pdl?.existingMonthlyDebtServiceCents),
      always: true,
    },
    {
      label: 'Available payment room',
      value: formatMoneyFromCents(pdl?.availableForNewPaymentCents),
      emphasize: true,
      always: true,
    },
    {
      label: 'Debt service ratio',
      value: formatRatioPercent(pdl?.debtServiceRatio),
      always: true,
    },
    {
      label: 'Policy version',
      value: pdl?.policyVersion ?? 'pdl-affordability.v1',
      always: true,
    },
  ];
}

export function buildPdlElodRiskRows(
  pdl?: PdlAffordabilityJourneyDto | null,
): DecisionPanelKvRow[] {
  return [
    {
      label: 'ELOD available',
      value: formatYesNo(pdl?.elodAvailable),
      always: true,
    },
    {
      label: 'Existing loan count',
      value:
        typeof pdl?.existingLoanCount === 'number' ? pdl.existingLoanCount : null,
      always: true,
    },
    {
      label: 'Payday stacking count',
      value:
        typeof pdl?.paydayStackingCount === 'number' ? pdl.paydayStackingCount : null,
      always: true,
    },
    {
      label: 'Debt-payment NSF (90d)',
      value:
        typeof pdl?.debtPaymentNsfCount90d === 'number'
          ? pdl.debtPaymentNsfCount90d
          : null,
      always: true,
    },
    {
      label: 'Loan velocity (90d)',
      value: typeof pdl?.loanVelocity90d === 'number' ? pdl.loanVelocity90d : null,
      always: true,
    },
    {
      label: 'Cash4You exposure',
      value: formatYesNo(pdl?.hasInternalC4YExposure),
      always: true,
    },
    {
      label: 'Detection confidence',
      value: pdl?.detectionConfidence ?? null,
      always: true,
    },
  ];
}

function formatSoftSignal(code: string): string {
  if (code === 'ELOD_UNAVAILABLE') return 'ELOD unavailable';
  if (code.startsWith('PAYDAY_STACKING:')) {
    const n = code.split(':')[1];
    return n ? `${n} payday lenders` : 'Payday stacking';
  }
  if (code.startsWith('DEBT_NSF:')) {
    const n = code.split(':')[1];
    return n ? `${n} returned payment(s) · 90d` : 'Returned payments';
  }
  if (code.startsWith('DEBT_SERVICE_RATIO:')) {
    const n = Number(code.split(':')[1]);
    return Number.isFinite(n) ? `Debt ratio ${(n * 100).toFixed(0)}%` : 'High debt ratio';
  }
  if (code.startsWith('LOAN_VELOCITY:')) {
    const n = code.split(':')[1];
    return n ? `${n} recent loan(s) · 90d` : 'Recent loan activity';
  }
  return code.replace(/_/g, ' ').toLowerCase();
}

export function buildPdlReviewReasonRows(
  pdl?: PdlAffordabilityJourneyDto | null,
): DecisionPanelKvRow[] {
  const reasons = pdl?.reviewReasons ?? [];
  if (reasons.length === 0) {
    return [{ label: 'Soft signals', value: 'None', always: true }];
  }
  return reasons.map((code, index) => ({
    label: index === 0 ? 'Soft signals' : '',
    value: formatSoftSignal(code),
    always: true,
  }));
}

export type PdlRemarkRow = {
  category: string;
  code: string;
  message: string;
};

export function buildPdlRemarkRows(
  pdl?: PdlAffordabilityJourneyDto | null,
): PdlRemarkRow[] {
  const remarks = pdl?.remarks ?? [];
  return remarks.map((r) => ({
    category: r.category,
    code: r.code,
    message: r.message,
  }));
}

export type PdlTraceRow = {
  code: string;
  label: string;
  detail: string;
  value: string;
};

export type { PdlAdjudicatorTraceRow };

export function formatPdlTraceLabel(code: string): string {
  return getPdlTraceLabel(code);
}

export function buildPdlTraceRows(pdl?: PdlAffordabilityJourneyDto | null): PdlTraceRow[] {
  return buildPdlAdjudicatorTraceRows(pdl).map(({ code, label, detail, value }) => ({
    code,
    label,
    detail,
    value,
  }));
}

export function buildPdlGroupedTraceSections(pdl?: PdlAffordabilityJourneyDto | null) {
  return groupPdlAdjudicatorTraceRows(buildPdlAdjudicatorTraceRows(pdl));
}

/** True when ELOD shows any risk signal worth surfacing in the detail panel. */
export function pdlElodHasRiskSignals(pdl?: PdlAffordabilityJourneyDto | null): boolean {
  if (!pdl) return false;
  if (pdl.hasInternalC4YExposure) return true;
  if ((pdl.paydayStackingCount ?? 0) >= 4) return true;
  if ((pdl.debtPaymentNsfCount90d ?? 0) > 0) return true;
  if ((pdl.loanVelocity90d ?? 0) > 0) return true;
  if (pdl.elodAvailable === false) return true;
  return false;
}

/** Plain-language ELOD notes for the adjudicator brief (review threshold and above). */
export function buildPdlElodElevatedNotes(pdl?: PdlAffordabilityJourneyDto | null): string[] {
  if (!pdlElodHasRiskSignals(pdl)) return [];
  const notes: string[] = [];
  if (pdl?.elodAvailable === false) {
    notes.push('ELOD unavailable — existing debt assumed $0');
  }
  if (pdl?.hasInternalC4YExposure) {
    notes.push('Cash4You loan detected on bank account');
  }
  const stacking = pdl?.paydayStackingCount ?? 0;
  if (stacking >= 4) {
    notes.push(`${stacking} payday lenders on account`);
  }
  const nsf = pdl?.debtPaymentNsfCount90d ?? 0;
  if (nsf > 0) {
    notes.push(`${nsf} returned payment(s) in 90 days`);
  }
  const velocity = pdl?.loanVelocity90d ?? 0;
  if (velocity > 0) {
    notes.push(`${velocity} recent loan(s) in 90 days`);
  }
  return notes;
}

export function resolvePdlDecisionCopy(
  decisionEngine?: DecisionEngineJourneyDto | null,
): { summary?: string | null; capacityReason?: string | null } {
  const pdl = decisionEngine?.pdlAffordability;
  return {
    summary: decisionEngine?.decisionSummary ?? pdl?.decisionSummary ?? null,
    capacityReason: decisionEngine?.capacityReason ?? pdl?.capacityReason ?? null,
  };
}
